// src/services/toneShiftApi.js
import { API_URL } from '../config';

// Transform base parameters (voices, roles, formats, audiences)
function transformBaseParameter(data) {
  return data.map(item => ({
    id: item.id,
    name: item.name,
    description: item.description,
    starter_prompt: item.starter_prompt,
    updated_prompt: item.updated_prompt,
    isCustom: item.is_custom,
    isDefault: item.is_default
  }));
}

// Transform modifiers with special level handling
function transformModifiers(modifiersData) {
  return modifiersData.map(modifier => {
    // Transform levels to the expected format and sort them properly
    const transformedLevels = (modifier.levels || [])
      .map(level => ({
        id: level.id,
        level_name: level.level_name,
        starter_prompt: level.starter_prompt,
        updated_prompt: level.updated_prompt,
        value: level.level_name
      }))
      .sort((a, b) => {
        // Sort levels from 1 to 6 (left to right)
        const levelOrder = {
          'most_collaborative': 1, 'more_collaborative': 2, 'collaborative': 3,
          'decisive': 4, 'more_decisive': 5, 'most_decisive': 6,
          'most_concise': 1, 'more_concise': 2, 'concise': 3,
          'detailed': 4, 'more_detailed': 5, 'most_detailed': 6,
          'most_professional': 1, 'more_professional': 2, 'professional': 3,
          'personal': 4, 'more_personal': 5, 'most_personal': 6,
          'most_diplomatic': 1, 'more_diplomatic': 2, 'diplomatic': 3,
          'blunt': 4, 'more_blunt': 5, 'most_blunt': 6,
          'most_calm': 1, 'more_calm': 2, 'calm': 3,
          'excited': 4, 'more_excited': 5, 'most_excited': 6,
          'most_casual': 1, 'more_casual': 2, 'casual': 3,
          'most_relaxed': 1, 'more_relaxed': 2, 'relaxed': 3,
          'immediate': 4, 'more_immediate': 5, 'most_immediate': 6,
          'most_humorous': 1, 'more_humorous': 2, 'humorous': 3,
          'serious': 4, 'more_serious': 5, 'most_serious': 6
        };
        return (levelOrder[a.level_name] || 0) - (levelOrder[b.level_name] || 0);
      });

    return {
      id: modifier.id,
      name: modifier.name,
      description: modifier.description,
      starter_prompt: modifier.starter_prompt,
      updated_prompt: modifier.updated_prompt,
      isCustom: modifier.is_custom,
      isDefault: modifier.is_default,
      levels: transformedLevels
    };
  });
}

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function http(path, { method = 'GET', json } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: json ? JSON.stringify(json) : undefined,
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
}

export const toneShiftApi = {
  // Catalog with integrated transformations
  async getVoices() { 
    const data = await http('/api/voices/');
    return transformBaseParameter(data);
  },
  async getRoles() { 
    const data = await http('/api/roles/');
    return transformBaseParameter(data);
  },
  async getFormats() { 
    const data = await http('/api/formats/');
    return transformBaseParameter(data);
  },
  async getAudiences() { 
    const data = await http('/api/audiences/');
    return transformBaseParameter(data);
  },
  async getModifiers() { 
    const data = await http('/api/modifiers/');
    return transformModifiers(data);
  },

  createVoice(json)     { return http('/api/voices/', { method: 'POST', json }); },
  updateVoice(id, json) { return http(`/api/voices/${id}/`, { method: 'PUT', json }); },

  createRole(json)      { return http('/api/roles/', { method: 'POST', json }); },
  updateRole(id, json)  { return http(`/api/roles/${id}/`, { method: 'PUT', json }); },

  createFormat(json)    { return http('/api/formats/', { method: 'POST', json }); },
  updateFormat(id, json){ return http(`/api/formats/${id}/`, { method: 'PUT', json }); },

  createAudience(json)  { return http('/api/audiences/', { method: 'POST', json }); },
  updateAudience(id, json){ return http(`/api/audiences/${id}/`, { method: 'PUT', json }); },

  // Modifiers (including levels)
  updateModifier(id, json) { return http(`/api/modifiers/${id}/`, { method: 'PUT', json }); },
  updateModifierLevel(id, json) { return http(`/api/modifier-levels/${id}/`, { method: 'PATCH', json }); },

  // Conversations
  createConversation(json) { return http('/api/tone-shift-conversations/', { method: 'POST', json }); },
  updateParameters(id, json) { return http(`/api/tone-shift-conversations/${id}/update_parameters/`, { method: 'POST', json }); },
  sendMessage(id, json) { return http(`/api/tone-shift-conversations/${id}/send_message/`, { method: 'POST', json }); },

  // Content
  createContent(json) { return http('/api/contents/', { method: 'POST', json }); },

  // Analyses
  async getAnalyses() {
    const data = await http('/api/analyses/');
    return data.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      starter_prompt: item.starter_prompt,
      updated_prompt: item.updated_prompt,
      isCustom: item.is_custom || false,
      isDefault: item.is_default || false
    }));
  },
  createAnalysis(json) { return http('/api/analyses/', { method: 'POST', json }); },
  updateAnalysis(id, json) { return http(`/api/analyses/${id}/`, { method: 'PUT', json }); },
  // Summaries
  async getSummaries() {
    const data = await http('/api/summaries/');
    return data.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      starter_prompt: item.starter_prompt,
      updated_prompt: item.updated_prompt,
      isCustom: item.is_custom || false,
      isDefault: item.is_default || false
    }));
  },
  createSummary(json) { return http('/api/summaries/', { method: 'POST', json }); },
  updateSummary(id, json) { return http(`/api/summaries/${id}/`, { method: 'PUT', json }); },
};
